import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  Typography,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon, Add as AddIcon } from '@mui/icons-material';

const AddTopicDialog = ({ open, onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    title: '',
    difficulty: 'unrated',
    notes: '',
  });
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      setError('Topic title is required');
      return;
    }

    try {
      await onAdd(formData);
      handleClose();
    } catch (error) {
      setError(error.message);
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      difficulty: 'unrated',
      notes: '',
    });
    setError('');
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: 'primary.main',
        color: 'white'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AddIcon sx={{ fontSize: 24 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Create New Topic
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
          Create a new topic to organize your topics and questions. Add a title, set the difficulty level, and include any helpful notes.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            fullWidth
            label="Topic Title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            autoFocus
            placeholder="e.g., Object-Oriented Programming, Collections Framework"
          />

          <TextField
            fullWidth
            label="Difficulty Level"
            name="difficulty"
            select
            value={formData.difficulty}
            onChange={handleChange}
            SelectProps={{
              native: true,
            }}
            helperText="Choose the difficulty level that best represents this topic"
          >
            <option value="unrated">Unrated</option>
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </TextField>

          <TextField
            fullWidth
            label="Notes (Optional)"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            multiline
            rows={4}
            placeholder="Add any notes, reminders, or additional context for this topic..."
            helperText="This will help you remember important points about this topic"
          />
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button 
          onClick={handleClose}
          sx={{ 
            px: 3, 
            py: 1.5,
            fontWeight: 600,
            borderRadius: 2
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          startIcon={<AddIcon />}
          sx={{ 
            px: 3, 
            py: 1.5,
            fontWeight: 600,
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
            '&:hover': {
              boxShadow: '0 6px 16px rgba(37, 99, 235, 0.4)',
            }
          }}
        >
          Create Topic
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTopicDialog; 